import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Search, Trash2, ArrowUp, ArrowDown, Save, Copy, Send, FileDown, FolderOpen } from 'lucide-react';
import { qd, po, money, apiError } from '../api';

const CATS = ['all', 'gateway', 'sensor', 'accessory'];
const emptyOrder = { entity_id: '', supplier_id: 'tektelic', ship_to: '', invoice_to: '', requested_date: '', incoterm: 'EXW', payment_terms: '', notes: '', items: [], shipping: 0, other: 0 };

export default function PoEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState({ ...emptyOrder });
  const [entities, setEntities] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('all');
  const [free, setFree] = useState({ ref: '', name: '', cost: '' });
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const formRef = useRef(form);
  formRef.current = form;
  const orderRef = useRef(order);
  orderRef.current = order;

  const isDraft = !order || order.status === 'draft';

  useEffect(() => {
    qd.get('/entities').then((r) => setEntities(r.data));
    po.get('/suppliers').then((r) => setSuppliers(r.data));
    po.get('/products').then((r) => setProducts(r.data));
    po.get('/templates').then((r) => setTemplates(r.data));
  }, []);

  useEffect(() => {
    if (id) {
      po.get(`/orders/${id}`).then((r) => {
        setOrder(r.data);
        setForm({ ...r.data, shipping: r.data.totals?.shipping || 0, other: r.data.totals?.other || 0 });
      });
    } else if (location.state?.template) {
      const t = location.state.template;
      setOrder(null);
      setForm({ ...emptyOrder, entity_id: t.entity_id, supplier_id: t.supplier_id, ship_to: t.ship_to, invoice_to: t.invoice_to || '', notes: t.notes || '', items: t.items || [] });
    } else {
      setOrder(null);
      setForm({ ...emptyOrder });
    }
  }, [id, location.state]);

  const setEntity = (eid) => {
    const e = entities.find((x) => x.id === eid);
    const addr = e ? `${e.name}\n${e.address}` : '';
    setForm((f) => ({
      ...f, entity_id: eid,
      ship_to: f.ship_to || addr,
      invoice_to: f.invoice_to || addr,
      payment_terms: f.payment_terms || localStorage.getItem(`po_payterms_${eid}`) || '',
    }));
  };

  const addProduct = (p) => setForm((f) => ({
    ...f,
    items: [...f.items, { ref: p.ref, name: p.name, qty: 1, unit_cost: Number(p.unit_cost_exw), price_overridden: false }],
  }));

  const addFree = () => {
    if (!free.name) return;
    setForm((f) => ({ ...f, items: [...f.items, { ref: free.ref, name: free.name, qty: 1, unit_cost: Number(free.cost) || 0, price_overridden: true }] }));
    setFree({ ref: '', name: '', cost: '' });
  };

  const setItem = (i, patch) => setForm((f) => {
    const items = [...f.items];
    items[i] = { ...items[i], ...patch };
    return { ...f, items };
  });
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, x) => x !== i) }));
  const moveItem = (i, dir) => setForm((f) => {
    const items = [...f.items];
    const j = i + dir;
    if (j < 0 || j >= items.length) return f;
    [items[i], items[j]] = [items[j], items[i]];
    return { ...f, items };
  });

  const subtotal = useMemo(() => form.items.reduce((a, it) => a + Math.round(it.qty * it.unit_cost * 100) / 100, 0), [form.items]);
  const grand = subtotal + (Number(form.shipping) || 0) + (Number(form.other) || 0);

  const payload = (f) => ({
    entity_id: f.entity_id, supplier_id: f.supplier_id, ship_to: f.ship_to, invoice_to: f.invoice_to || '',
    requested_date: f.requested_date || null, incoterm: f.incoterm, payment_terms: f.payment_terms,
    notes: f.notes, shipping: Number(f.shipping) || 0, other: Number(f.other) || 0,
    items: f.items.map((it) => ({ ref: it.ref || '', name: it.name, qty: Math.max(1, parseInt(it.qty) || 1), unit_cost: Number(it.unit_cost) || 0, price_overridden: !!it.price_overridden })),
  });

  const saveDraft = useCallback(async (silent = false) => {
    const f = formRef.current;
    const o = orderRef.current;
    if (!f.entity_id || !f.supplier_id) { if (!silent) setMsg('Select an entity and a supplier first.'); return null; }
    try {
      if (f.entity_id && f.payment_terms) localStorage.setItem(`po_payterms_${f.entity_id}`, f.payment_terms);
      let data;
      if (o?.id) ({ data } = await po.patch(`/orders/${o.id}`, payload(f)));
      else {
        ({ data } = await po.post('/orders', payload(f)));
        navigate(`/quote/po/${data.id}`, { replace: true });
      }
      setOrder(data);
      if (!silent) setMsg(o && o.status !== 'draft' ? `${data.number} updated — PDF regenerated.` : 'Draft saved.');
      return data;
    } catch (e) { if (!silent) setMsg(apiError(e)); return null; }
  }, [navigate]);

  useEffect(() => {
    const t = setInterval(() => {
      if (orderRef.current && orderRef.current.status === 'draft') saveDraft(true);
    }, 30000);
    return () => clearInterval(t);
  }, [saveDraft]);

  const saveTemplate = async () => {
    const name = window.prompt('Template name:');
    if (!name) return;
    try {
      await po.post('/templates', { name, entity_id: form.entity_id, supplier_id: form.supplier_id, ship_to: form.ship_to, invoice_to: form.invoice_to || '', items: payload(form).items, notes: form.notes });
      po.get('/templates').then((r) => setTemplates(r.data));
      setMsg(`Template "${name}" saved.`);
    } catch (e) { setMsg(apiError(e)); }
  };

  const loadTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    if (!window.confirm(`Load template "${t.name}"? Current lines will be replaced.`)) return;
    setForm((f) => ({ ...f, entity_id: t.entity_id, supplier_id: t.supplier_id, ship_to: t.ship_to, invoice_to: t.invoice_to || f.invoice_to || '', notes: t.notes || '', items: t.items || [] }));
  };

  const issue = async () => {
    if (busy) return;
    if (!form.entity_id || !form.supplier_id) { setMsg('Entity and supplier are required.'); return; }
    if (!form.items.length) { setMsg('At least one line is required.'); return; }
    setBusy(true);
    try {
      const saved = await saveDraft(true);
      if (!saved) { setBusy(false); return; }
      const { data } = await po.post(`/orders/${saved.id}/issue`);
      setOrder(data);
      setForm({ ...data, shipping: data.totals?.shipping || 0, other: data.totals?.other || 0 });
      setMsg(`PO issued: ${data.number}`);
      await downloadPdf(data);
    } catch (e) { setMsg(apiError(e)); }
    finally { setBusy(false); }
  };

  const downloadPdf = async (o = order) => {
    try {
      const { data } = await po.get(`/orders/${o.id}/pdf`, { responseType: 'blob' });
      const u = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      window.open(u, '_blank');
      setTimeout(() => URL.revokeObjectURL(u), 30000);
    } catch (e) { setMsg(apiError(e)); }
  };

  const [sendOpen, setSendOpen] = useState(false);
  const [sendChecks, setSendChecks] = useState({});

  const supplierContacts = (suppliers.find((s) => s.id === (order?.supplier_id || form.supplier_id))?.contacts) || [];

  const openSend = () => {
    setSendChecks({});
    setSendOpen(true);
  };

  const sendToMe = async () => {
    if (busy || !order) return;
    setBusy(true);
    setMsg('Sending…');
    try {
      const recipients = Object.entries(sendChecks).filter(([, v]) => v).map(([k]) => k);
      const { data } = await po.post(`/orders/${order.id}/send`, { recipients });
      setOrder((o) => ({ ...o, status: 'sent', sent_to: data.sent_to }));
      setMsg(`✓ ${data.number} sent to ${data.sent_to}`);
      setSendOpen(false);
    } catch (e) { setMsg(apiError(e)); }
    finally { setBusy(false); }
  };

  const dup = async () => {
    const { data } = await po.post(`/orders/${order.id}/duplicate`);
    navigate(`/quote/po/${data.id}`);
  };

  const shownProducts = products.filter((p) => {
    if (!p.active) return false;
    if (cat !== 'all' && p.category !== cat) return false;
    const s = search.trim().toLowerCase();
    return !s || p.ref.toLowerCase().includes(s) || p.name.toLowerCase().includes(s);
  });

  return (
    <div className="flex min-h-screen" data-testid="po-editor-page">
      {/* Catalog pane */}
      <div className="w-80 flex-shrink-0 border-r border-slate-200 bg-white flex flex-col">
          <div className="p-4 border-b border-slate-100">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Supplier catalogue (EXW)</p>
            <div className="relative mb-2">
              <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-2.5" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ref or name…"
                className="w-full border border-slate-300 rounded pl-8 pr-2 py-2 text-sm" data-testid="po-picker-search" />
            </div>
            <div className="flex gap-1 flex-wrap">
              {CATS.map((c) => (
                <button key={c} onClick={() => setCat(c)}
                  className={`px-2 py-1 text-[11px] rounded border capitalize ${cat === c ? 'bg-[#14233F] text-white border-[#14233F]' : 'border-slate-200 text-slate-500'}`}>{c}</button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {shownProducts.map((p) => (
              <button key={p.id} onClick={() => addProduct(p)}
                className="w-full text-left px-4 py-2.5 border-b border-slate-50 hover:bg-teal-50/60"
                data-testid={`po-pick-${p.ref}`}>
                <p className="mono text-[11px] text-slate-400">{p.ref} · {p.category}</p>
                <p className="text-sm text-slate-800 leading-tight">{p.name}</p>
                <p className="mono text-xs text-[#0F7D70] font-semibold">{Number(p.unit_cost_exw).toFixed(2)} €</p>
              </button>
            ))}
            {shownProducts.length === 0 && <p className="p-4 text-xs text-slate-400">No products.</p>}
          </div>
          <div className="p-3 border-t border-slate-100">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Free line</p>
            <div className="flex gap-1.5 mb-1.5">
              <input value={free.ref} onChange={(e) => setFree({ ...free, ref: e.target.value })} placeholder="Ref" className="w-20 border border-slate-300 rounded px-2 py-1.5 text-xs" data-testid="po-free-ref" />
              <input value={free.name} onChange={(e) => setFree({ ...free, name: e.target.value })} placeholder="Description" className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-xs" data-testid="po-free-name" />
            </div>
            <div className="flex gap-1.5">
              <input type="number" value={free.cost} onChange={(e) => setFree({ ...free, cost: e.target.value })} placeholder="Unit cost €" className="w-24 border border-slate-300 rounded px-2 py-1.5 text-xs" data-testid="po-free-cost" />
              <button onClick={addFree} className="flex-1 bg-[#14233F] text-white text-xs rounded py-1.5" data-testid="po-free-add">Add line</button>
            </div>
          </div>
        </div>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between flex-wrap gap-2 sticky top-0 z-10">
          <div>
            <span className="font-bold text-[#14233F]">{order?.number || 'New Purchase Order'}</span>
            {order && <span className={`ml-2 inline-flex px-2 py-0.5 rounded text-[11px] font-semibold capitalize ${order.status === 'draft' ? 'bg-slate-100 text-slate-600' : order.status === 'issued' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'}`}>{order.status}</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select onChange={(e) => { if (e.target.value) { loadTemplate(e.target.value); e.target.value = ''; } }}
              className="border border-slate-300 rounded px-2 py-2 text-xs" defaultValue="" data-testid="po-load-template">
              <option value="" disabled>Load template…</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button onClick={saveTemplate} className="inline-flex items-center gap-1.5 border border-slate-300 px-3 py-2 rounded text-xs font-semibold text-slate-600" data-testid="po-save-template">
              <FolderOpen className="h-3.5 w-3.5" /> Save as template
            </button>
            <button onClick={() => saveDraft()} className="inline-flex items-center gap-1.5 border border-[#14233F] text-[#14233F] px-3 py-2 rounded text-xs font-semibold" data-testid="po-save-draft">
              <Save className="h-3.5 w-3.5" /> {isDraft ? 'Save draft' : 'Save changes'}
            </button>
            {isDraft && (
              <button onClick={issue} disabled={busy} className="inline-flex items-center gap-1.5 bg-[#14233F] text-white px-4 py-2 rounded text-xs font-semibold disabled:opacity-40" data-testid="po-issue-btn">
                Issue PO
              </button>
            )}
            {order && order.status !== 'draft' && (
              <>
                <button onClick={() => downloadPdf()} className="inline-flex items-center gap-1.5 border border-slate-300 px-3 py-2 rounded text-xs font-semibold text-slate-600" data-testid="po-pdf-btn">
                  <FileDown className="h-3.5 w-3.5" /> PDF
                </button>
                <button onClick={dup} className="inline-flex items-center gap-1.5 border border-slate-300 px-3 py-2 rounded text-xs font-semibold text-slate-600" data-testid="po-dup-btn">
                  <Copy className="h-3.5 w-3.5" /> Duplicate
                </button>
                <button onClick={openSend} disabled={busy} className="inline-flex items-center gap-1.5 bg-[#0F7D70] text-white px-4 py-2 rounded text-xs font-semibold disabled:opacity-40" data-testid="po-send-btn">
                  <Send className="h-3.5 w-3.5" /> Send
                </button>
              </>
            )}
          </div>
        </div>
        {msg && <p className="px-6 pt-2 text-xs font-semibold text-[#0F7D70]" data-testid="po-editor-msg">{msg}</p>}

        {sendOpen && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="po-send-modal">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <h2 className="text-lg font-bold text-[#14233F] mb-1">Send {order?.number}</h2>
              <p className="text-xs text-slate-400 mb-4">From atlas@0gbaltics.com · Reply-To eric@0gbaltics.com · PDF attached</p>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Recipients</label>
              <div className="border border-slate-200 rounded p-3 mb-2 flex items-center gap-2.5 bg-slate-50 text-sm">
                <input type="checkbox" checked readOnly disabled className="opacity-60" />
                <span className="font-semibold text-slate-700">eric@0gbaltics.com</span>
                <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400">always</span>
              </div>
              {supplierContacts.map((c) => (
                <label key={c.email} className="border rounded p-3 mb-2 flex items-center gap-2.5 text-sm cursor-pointer border-slate-200 has-[:checked]:border-[#0F7D70] has-[:checked]:bg-teal-50/50">
                  <input type="checkbox" checked={!!sendChecks[c.email]}
                    onChange={(e) => setSendChecks((p) => ({ ...p, [c.email]: e.target.checked }))}
                    data-testid={`po-recipient-${c.email}`} />
                  <span>
                    <span className="font-semibold text-slate-800 block">{c.name}</span>
                    <span className="text-xs text-slate-500">{c.email}</span>
                  </span>
                </label>
              ))}
              {supplierContacts.length === 0 && <p className="text-xs text-slate-400 mb-2">No supplier contacts on file — the PO will be sent to you only.</p>}
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setSendOpen(false)} className="border border-slate-300 px-4 py-2 rounded text-sm text-slate-600" data-testid="po-send-cancel">Cancel</button>
                <button onClick={sendToMe} disabled={busy} className="bg-[#0F7D70] text-white px-5 py-2 rounded text-sm font-semibold disabled:opacity-40" data-testid="po-send-confirm">
                  {busy ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="p-6 max-w-4xl">
          <div className="bg-white border border-slate-200 rounded p-5 mb-5">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Entity</label>
                <select value={form.entity_id} onChange={(e) => setEntity(e.target.value)}
                  className="w-full border border-slate-300 rounded px-2 py-2 text-sm" data-testid="po-entity-select">
                  <option value="">Select…</option>
                  {entities.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.country})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Supplier</label>
                <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
                  className="w-full border border-slate-300 rounded px-2 py-2 text-sm" data-testid="po-supplier-select">
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Requested delivery</label>
                <input type="date" value={form.requested_date || ''} onChange={(e) => setForm({ ...form, requested_date: e.target.value })}
                  className="w-full border border-slate-300 rounded px-2 py-2 text-sm" data-testid="po-date" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Incoterm</label>
                <input value={form.incoterm} onChange={(e) => setForm({ ...form, incoterm: e.target.value })}
                  className="w-full border border-slate-300 rounded px-2 py-2 text-sm" data-testid="po-incoterm" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Payment terms</label>
                <input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                  className="w-full border border-slate-300 rounded px-2 py-2 text-sm" data-testid="po-payterms" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Currency</label>
                <input value="EUR" readOnly className="w-full border border-slate-200 bg-slate-50 rounded px-2 py-2 text-sm text-slate-500" data-testid="po-currency" />
              </div>
              <div className="col-span-2 md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Ship to</label>
                  <textarea value={form.ship_to} onChange={(e) => setForm({ ...form, ship_to: e.target.value })} rows={3}
                    className="w-full border border-slate-300 rounded px-2 py-2 text-sm" data-testid="po-shipto" />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Invoice to</label>
                  <textarea value={form.invoice_to || ''} onChange={(e) => setForm({ ...form, invoice_to: e.target.value })} rows={3}
                    placeholder="Billing address (if different from Ship to)"
                    className="w-full border border-slate-300 rounded px-2 py-2 text-sm" data-testid="po-invoiceto" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded overflow-x-auto mb-5">
            <table className="w-full text-sm min-w-[640px]" data-testid="po-items-table">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left px-3 py-2.5 font-semibold w-8">#</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Ref</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Description</th>
                  <th className="text-right px-3 py-2.5 font-semibold w-20">Qty</th>
                  <th className="text-right px-3 py-2.5 font-semibold w-28">Unit cost €</th>
                  <th className="text-right px-3 py-2.5 font-semibold w-28">Line total €</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-3 py-2 mono text-xs text-slate-400">{i + 1}</td>
                    <td className="px-3 py-2 mono text-xs">{it.ref}</td>
                    <td className="px-3 py-2">{it.name}{it.price_overridden && <span className="ml-1.5 text-[10px] text-amber-600 font-semibold" title="Negotiated price">●</span>}</td>
                    <td className="px-3 py-2 text-right">
                      <input type="number" min="1" value={it.qty}
                        onChange={(e) => setItem(i, { qty: Math.max(1, parseInt(e.target.value) || 1) })}
                        className="w-16 border border-slate-300 rounded px-1.5 py-1 text-sm text-right mono" data-testid={`po-qty-${i}`} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input type="number" step="0.01" value={it.unit_cost}
                        onChange={(e) => setItem(i, { unit_cost: e.target.value, price_overridden: true })}
                        className="w-24 border border-slate-300 rounded px-1.5 py-1 text-sm text-right mono" data-testid={`po-cost-${i}`} />
                    </td>
                    <td className="px-3 py-2 text-right mono">{(Math.round(it.qty * it.unit_cost * 100) / 100).toFixed(2)}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <button onClick={() => moveItem(i, -1)} title="Move up"><ArrowUp className="h-3.5 w-3.5 text-slate-300 hover:text-slate-600 inline" /></button>
                      <button onClick={() => moveItem(i, 1)} title="Move down" className="ml-1"><ArrowDown className="h-3.5 w-3.5 text-slate-300 hover:text-slate-600 inline" /></button>
                      <button onClick={() => removeItem(i)} title="Remove" className="ml-1.5" data-testid={`po-rm-${i}`}><Trash2 className="h-3.5 w-3.5 text-slate-300 hover:text-red-600 inline" /></button>
                    </td>
                  </tr>
                ))}
                {form.items.length === 0 && <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400 text-sm">Pick products from the catalogue on the left.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row gap-5">
            <div className="flex-1">
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Notes (printed on PDF)</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm" data-testid="po-notes" />
            </div>
            <div className="w-full md:w-72 bg-white border border-slate-200 rounded p-4 space-y-2 text-sm" data-testid="po-totals">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="mono">{money(subtotal)}</span></div>
              <div className="flex justify-between items-center"><span className="text-slate-500">Shipping</span>
                <input type="number" step="0.01" value={form.shipping} onChange={(e) => setForm({ ...form, shipping: e.target.value })}
                  className="w-24 border border-slate-300 rounded px-1.5 py-1 text-sm text-right mono" data-testid="po-shipping" />
              </div>
              <div className="flex justify-between items-center"><span className="text-slate-500">Other</span>
                <input type="number" step="0.01" value={form.other} onChange={(e) => setForm({ ...form, other: e.target.value })}
                  className="w-24 border border-slate-300 rounded px-1.5 py-1 text-sm text-right mono" data-testid="po-other" />
              </div>
              <div className="flex justify-between border-t border-slate-200 pt-2 font-bold text-[#14233F]">
                <span>Grand total EUR</span><span className="mono" data-testid="po-grand">{money(grand)}</span>
              </div>
              <p className="text-[10px] text-slate-400">No VAT — handled at customs clearance (import).</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
